// The poster page's zoomable image (PosterViewer.vue; spec 2026-10-26): OpenSeadragon on the
// old server's Deep Zoom pyramids, loading the low levels first and sharpening as tiles arrive
// (its own multiresolution loading, not tuned). On the page the image is only shown: no zoom or
// pan, the page scrolls over it, a click opens full screen (refined 2026-10-26); full screen,
// OpenSeadragon's own, is where it's explored: a click zooms in (⇧ out), the wheel and a pinch
// zoom gently, a drag pans; our own controls (PosterViewer.vue).
import OpenSeadragon from "openseadragon";
import {getOverviewUrl} from "@/composables/usePosters.js";

const NAVIGATOR_FROM = 1.2; // × the fitted zoom: the navigator shows from here on
const ZOOM_STEP = 1.6; // a click or a + press (OpenSeadragon's 2 felt abrupt)

export function getViewer(element) {
  return OpenSeadragon({
    element,
    drawer: "canvas", // the tiles are on another origin, without the CORS headers WebGL needs
    // the tiles are PNGs, so drawn as transparent: each clears its place first, at fractional
    // pixels, leaving faint seams between them unless rounded to whole ones
    subPixelRoundingForTransparency: OpenSeadragon.SUBPIXEL_ROUNDING_OCCURRENCES.ALWAYS,
    cooperativeGestures: true, // with navigation off: one finger scrolls the page on a phone
    mouseNavEnabled: false, // on until full screen (setInteractive)
    showNavigationControl: false, // our own controls (PosterViewer.vue)
    showNavigator: true,
    navigatorPosition: "BOTTOM_RIGHT",
    navigatorSizeRatio: 0.15,
    visibilityRatio: 1, // the poster can't be dragged out of sight
    zoomPerClick: ZOOM_STEP,
    zoomPerScroll: 1.1, // a wheel or trackpad step (its 1.2 raced away on a trackpad)
    gestureSettingsMouse: {clickToZoom: true, dblClickToZoom: false},
    gestureSettingsTouch: {clickToZoom: false, dblClickToZoom: true},
  });
}

// the poster's one-tile overview fetched before its other tiles, so it's there at once, whole,
// under those that sharpen it (fetched together, they share the connection and come in patches)
export function fetchOverview(poster) {
  return new Promise((resolve) => {
    if (!poster.pyramid) return resolve();
    const image = new Image();
    image.onload = image.onerror = resolve;
    image.src = getOverviewUrl(poster);
  });
}

// full screen: the image explored directly; the page: shown only
export const setInteractive = (viewer, isOn) => viewer.setMouseNavEnabled(isOn);
export const zoomIn = (viewer) => viewer.viewport.zoomBy(ZOOM_STEP).applyConstraints();
export const zoomOut = (viewer) => viewer.viewport.zoomBy(1 / ZOOM_STEP).applyConstraints();
export const fitImage = (viewer) => viewer.viewport.goHome();

// the box changed size: measured at once (OpenSeadragon would notice on its next frame), refitted
export function refit(viewer) {
  viewer.forceResize();
  viewer.viewport.goHome(true);
}

// the navigator only once zoomed in, where it helps to see where you are
export function updateNavigator(viewer) {
  const element = viewer.navigator?.element;
  if (!element) return;
  const isZoomedIn = viewer.viewport.getZoom() > viewer.viewport.getHomeZoom() * NAVIGATOR_FROM;
  element.style.display = isZoomedIn ? "" : "none";
}
